import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, Typography, Button } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MainCarousel from "../MainCarousel/MainCarousel";
import { useDispatch, useSelector } from "react-redux";
import { getUsedBikeDetails } from "../../State/UsedBike/Action";
import { getStatesWithCities } from "../../State/State/Action";
import { 
  HiMapPin, 
  HiPhone, 
  HiUser, 
  HiCalendar,
  HiCog,
  HiPaintBrush,
  HiCurrencyRupee,
  HiDocumentText,
  HiShieldCheck,
  HiHeart,
  HiArrowLeft,
  HiInformationCircle,
  HiEye
} from "react-icons/hi2";

export default function UsedBikeDetail() {
  const [activeTab, setActiveTab] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const bikeId = new URLSearchParams(location.search).get("bikeId");
  const bike = useSelector((store) => store.usedBikesResponse.bike);
  const images = useSelector((store) => store.usedBikesResponse.bike.images);
  const dispatch = useDispatch();
  const { statesWithCities = [] } = useSelector((store) => store.stateResponse || {});

  useEffect(() => {
    dispatch(getUsedBikeDetails(bikeId));
    // Fetch states with cities to get state name
    dispatch(getStatesWithCities());
  }, [bikeId, dispatch]);

  // Find state name from city
  const sellerStateName = useMemo(() => {
    if (!bike || !bike.sellerCityId) return null;
    
    // First check if bike has state name directly
    if (bike.sellerState || bike.stateName || bike.sellerStateName) {
      return bike.sellerState || bike.stateName || bike.sellerStateName;
    }
    
    // Otherwise, find state from cityId in statesWithCities
    for (const state of statesWithCities) {
      const city = (state.cities || []).find(c => c.cityId === bike.sellerCityId);
      if (city) {
        return state.stateName;
      }
    }
    
    return null;
  }, [bike, statesWithCities]);

  const tabs = [
    { id: 0, label: "Bike Details", icon: <HiInformationCircle className="w-5 h-5" /> },
    { id: 1, label: "Seller Details", icon: <HiUser className="w-5 h-5" /> }
  ];

  const bikeDetails = [
    { label: "Brand", value: bike?.brand, icon: <HiCog className="w-5 h-5" /> },
    { label: "Model", value: bike?.model, icon: <HiDocumentText className="w-5 h-5" /> },
    { label: "Color", value: bike?.color, icon: <HiPaintBrush className="w-5 h-5" /> },
    { label: "Price", value: `₹${bike?.price}`, icon: <HiCurrencyRupee className="w-5 h-5" /> },
    { label: "Running KM", value: bike?.runningKms === 0 ? "NA" : bike?.runningKms, icon: <HiCog className="w-5 h-5" /> },
    { label: "Purchase Year", value: bike?.purchaseYear, icon: <HiCalendar className="w-5 h-5" /> },
    { label: "Model Year", value: bike?.modelYear, icon: <HiCalendar className="w-5 h-5" /> },
    { label: "Registration", value: bike?.registrationNumber, icon: <HiDocumentText className="w-5 h-5" /> }
  ];

  const sellerDetails = [
    { label: "Seller Name", value: bike?.sellerName, icon: <HiUser className="w-5 h-5" /> },
    { label: "Contact", value: bike?.sellerMobile, icon: <HiPhone className="w-5 h-5" /> },
    { label: "Address", value: bike?.sellerAddress, icon: <HiMapPin className="w-5 h-5" /> },
    { label: "City", value: bike?.sellerCity, icon: <HiMapPin className="w-5 h-5" /> },
    ...(sellerStateName ? [{ label: "State", value: sellerStateName, icon: <HiMapPin className="w-5 h-5" /> }] : [])
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {bikeDetails.map((detail, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="card card-hover p-4">
                    <CardContent>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="text-primary-600">
                          {detail.icon}
                        </div>
                        <Typography variant="h6" className="font-semibold text-gray-900">
                          {detail.label}
                        </Typography>
                      </div>
                      <Typography variant="body1" className="text-gray-700">
                        {detail.value || "Not specified"}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            
            {bike?.description && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.8 }}
                className="mt-6"
              >
                <Card className="card card-hover p-6">
                  <CardContent>
                    <div className="flex items-center gap-3 mb-4">
                      <HiDocumentText className="w-6 h-6 text-primary-600" />
                      <Typography variant="h6" className="font-semibold text-gray-900">
                        Description
                      </Typography>
                    </div>
                    <Typography variant="body1" className="text-gray-700 leading-relaxed">
                      {bike.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </motion.div>
        );
      
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sellerDetails.map((detail, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="card card-hover p-4">
                    <CardContent>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="text-primary-600">
                          {detail.icon}
                        </div>
                        <Typography variant="h6" className="font-semibold text-gray-900">
                          {detail.label}
                        </Typography>
                      </div>
                      <Typography variant="body1" className="text-gray-700">
                        {detail.value || "Not provided"}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        );
      
      default:
        return null;
    }
  };

  if (!bike) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <Typography variant="h6" className="text-gray-600">
            Loading bike details...
          </Typography>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Compact Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-primary-600 to-accent-600 text-white py-2 px-4"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            className="border-white text-white hover:bg-white hover:text-primary-600 rounded-lg px-3 py-1"
            startIcon={<HiArrowLeft className="w-4 h-4" />}
          >
            Back
          </Button>
          <div className="text-center">
            <Typography variant="h6" className="font-bold" sx={{ fontSize: "1.25rem" }}>
              {bike.brand} {bike.model}
            </Typography>
            <Typography variant="body2" className="opacity-90" sx={{ fontSize: "0.875rem" }}>
              {bike.color} • {bike.runningKms || "NA"} KM
            </Typography>
          </div>
          <Button
            variant="outlined"
            className="border-white text-white hover:bg-white hover:text-primary-600 rounded-lg px-3 py-1"
            startIcon={<HiHeart className="w-4 h-4" />}
          >
            Save
          </Button>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Image Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-4"
        >
          <Card className="bg-white rounded-2xl shadow-xl border-0 overflow-hidden" sx={{ borderRadius: "16px" }}>
            {images && <MainCarousel imageList={images} />}
          </Card>
        </motion.div>

        {/* Price Card - Label Above Price */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-6"
        >
          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl shadow-lg border-0" sx={{ borderRadius: "12px" }}>
            <CardContent className="px-4 py-1 text-center">
              <Typography variant="body2" className="opacity-80 mb-0.5" sx={{ fontSize: "0.7rem" }}>
                Asking Price
              </Typography>
              <div className="flex items-center justify-center">
                <Typography variant="h5" className="font-bold" sx={{ fontSize: "1.5rem" }}>
                  ₹{bike.price?.toLocaleString()}
                </Typography>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Custom Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-8"
        >
          <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
            <CardContent className="p-6">
              <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent" style={{ scrollbarWidth: 'thin' }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap text-sm ${
                      activeTab === tab.id
                        ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
              
              <AnimatePresence mode="wait">
                {renderTabContent()}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mb-8"
        >
          <Card className="bg-yellow-50 border border-yellow-200 rounded-xl" sx={{ borderRadius: "12px" }}>
            <CardContent className="p-4">
              <div className="flex items-start">
                <div className="p-1 bg-yellow-100 rounded-lg mr-3 mt-0.5">
                  <HiShieldCheck className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <Typography variant="h6" className="font-semibold text-yellow-800 mb-1" sx={{ fontSize: "1rem" }}>
                    Important Notice
                  </Typography>
                  <Typography variant="body2" className="text-yellow-700" sx={{ fontSize: "0.875rem" }}>
                    BlueBike does not verify the authenticity of any bike. Please check all details and documents with the seller before making any purchase decisions.
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            variant="contained"
            size="large"
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            startIcon={<HiEye className="w-5 h-5" />}
            onClick={() => window.open(`tel:${bike.sellerMobile}`)}
          >
            Contact Seller
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate(-1)}
            className="border-2 border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-white rounded-xl px-8 py-4 font-semibold transition-all duration-300"
            startIcon={<HiArrowLeft className="w-5 h-5" />}
          >
            Back to Listings
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

